import { ArrowUpRight, LockKeyhole } from "lucide-react";
import { Link } from "react-router-dom";
import { EmptyState } from "./Ui";
import type { Initiative } from "../types";
import { label } from "../utils";
import { usePermissions } from "../permissions";

export function InitiativeTable({ items }: { items: Initiative[] }) {
  const permissions = usePermissions();
  if (!items.length)
    return (
      <EmptyState
        title="No initiatives found"
        description="Adjust the filters or submit a new initiative."
      />
    );
  return (
    <div className="table-wrap" tabIndex={0} aria-label="Initiatives table">
      <table className="initiative-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Initiative</th>
            <th>Category</th>
            <th>Team</th>
            <th>Lifecycle</th>
            {permissions.businessCan("score.view",true) && <th>Adjusted RICE</th>}
            <th>Freshness</th>
            {permissions.can("governance.view") && <th>Board</th>}
            <th>
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td className="col-id" data-label="ID">
                {item.publicId}
              </td>
              <td className="col-name" data-label="Initiative">
                <Link to={`/initiatives/${item.publicId}`}>
                  {item.confidentiality === "confidential" && (
                    <LockKeyhole size={12} aria-label="Confidential" />
                  )}
                  {item.name}
                </Link>
                <small>{item.strategicDriver}</small>
              </td>
              <td data-label="Category">
                <span
                  className={`cat-badge cat-${item.category.toLowerCase()}`}
                >
                  {item.category}
                </span>
              </td>
              <td data-label="Team">{item.team}</td>
              <td data-label="Lifecycle">
                <span className={`status-badge status-${item.workflowState}`}>
                  {label(
                    item.decisionOutcome ??
                      item.deliveryState ??
                      item.workflowState,
                  )}
                </span>
              </td>
              {permissions.businessCan("score.view",true) && <td
                data-label="Adjusted RICE"
                className={`rice-${item.priorityBand ?? "none"}`}
              >
                {item.adjustedRice?.toFixed(1) ?? "—"}
              </td>}
              <td data-label="Freshness">
                <span className={`age-badge age-${item.freshness.status}`}>
                  {item.freshness.days == null
                    ? "Not reviewed"
                    : `${label(item.freshness.status)} · ${item.freshness.days}d`}
                </span>
              </td>
              {permissions.can("governance.view") && (
                <td data-label="Board">
                  {item.boardState === "not_required" ? (
                    <span className="muted">—</span>
                  ) : (
                    <span className="board-badge">
                      {label(item.boardState)}
                    </span>
                  )}
                </td>
              )}
              <td className="mobile-row-action">
                <Link
                  className="row-open"
                  to={`/initiatives/${item.publicId}`}
                  aria-label={`Open ${item.name}`}
                >
                  <ArrowUpRight size={15} />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
